import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Snackbar,
} from '@mui/material';
import { Add, Delete, Business } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TableShimmer } from '../../components/common/Shimmer';
import clusterService from '../../services/clusterService';
import { Cluster, CreateClusterRequest } from '../../types';

const clusterSchema = yup.object({
  name: yup.string().required('Cluster name is required'),
  code: yup.string().required('Cluster code is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
});

const ClustersPage: React.FC = () => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateClusterRequest>({
    resolver: yupResolver(clusterSchema) as any,
  });

  // Helper functions for snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchClusters = async () => {
    try {
      setLoading(true);
      const response = await clusterService.getClusters();
      setClusters(response.clusters);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch clusters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClusters();
  }, []);

  const handleCreateCluster = async (data: CreateClusterRequest) => {
    try {
      const response = await clusterService.createCluster(data);
      setClusters([...clusters, response]);
      setOpenDialog(false);
      reset();
      setError(null);
      showSnackbar('Cluster created successfully!', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create cluster';
      
      // Handle specific backend validation errors
      if (errorMessage.includes('already exists')) {
        showSnackbar('Cluster code already exists. Please use a different code.', 'warning');
      } else if (errorMessage.includes('Cluster Level Manager')) {
        showSnackbar('Only Cluster Level Manager can create clusters.', 'error');
      } else {
        showSnackbar(errorMessage, 'error');
      }
    }
  };

  // DISABLED: cluster deletion is not permitted for anyone - see the disabled
  // delete IconButton in the table below.
  // const handleDeleteCluster = async (clusterId: string) => {
  //   if (window.confirm('Are you sure you want to delete this cluster?')) {
  //     try {
  //       await clusterService.deleteCluster(clusterId);
  //       setClusters(clusters.filter(cluster => cluster.id !== clusterId));
  //       showSnackbar('Cluster deleted successfully!', 'success');
  //     } catch (err: any) {
  //       showSnackbar(err.response?.data?.message || 'Failed to delete cluster', 'error');
  //     }
  //   }
  // };

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Cluster Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage clusters and regional operations
          </Typography>
        </Box>
        <TableShimmer rows={5} columns={6} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Clusters Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage your cluster locations and details
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
            },
            boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
            px: 3,
            py: 1.5,
          }}
        >
          Add Cluster
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clusters.map((cluster) => (
              <TableRow key={cluster.id}>
                <TableCell>
                  <Chip
                    label={cluster.code}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{cluster.name}</TableCell>
                <TableCell>{cluster.address || 'N/A'}</TableCell>
                <TableCell>{cluster.manager_id || 'N/A'}</TableCell>
                <TableCell>{new Date(cluster.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  {/* DISABLED: Cluster deletion is not permitted for anyone.
                      ClusterService.deleteCluster cascades: it hard-deletes EVERY store in
                      the cluster and, with them, all of those stores' attendance and leave
                      records, then unassigns every user (cluster_id = null) and detaches
                      every shift. Deleting "Cluster A" alone would hit 7 stores / 900+ users.
                      Do not re-enable without a soft-delete/archive path on the backend.
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteCluster(cluster.id)}
                    title="Delete Cluster"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                  */}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Cluster Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: '#2196f3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <Business />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                Create New Cluster
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Add a new cluster to your organization
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit(handleCreateCluster)}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  {...register('name')}
                  label="Cluster Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  placeholder="e.g., North Delhi Cluster"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('code')}
                  label="Cluster Code"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  placeholder="e.g., NDC001"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('city')}
                  label="City"
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  placeholder="e.g., Delhi"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('state')}
                  label="State"
                  fullWidth
                  error={!!errors.state}
                  helperText={errors.state?.message}
                  placeholder="e.g., Delhi"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            gap: 2
          }}>
            <Button 
              onClick={() => {
                setOpenDialog(false);
                reset();
              }}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                borderColor: '#d0d0d0',
                color: '#666666',
                '&:hover': {
                  borderColor: '#999999',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Create Cluster
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClustersPage;
